"use client"

import { useSyncExternalStore } from "react"

import { mockNotifications } from "@/features/v2/notifications/data/mockNotifications"
import type { Notification } from "@/features/v2/notifications/types"

import { screenTexts } from "./localization/screen"
import { NotificationsScreen } from "./NotificationsScreen"
import { showNotificationMocks } from "./preview"

// Tablet is `md` (768px) up to, but not including, `xlg`. The bottom nav stays
// until `ls` (900px) and paints above this panel, so below `ls` the max height
// stops above that bar.
const tabletQuery = "(min-width: 768px) and (max-width: 1279.98px)"

function subscribe(onChange: () => void) {
  const media = window.matchMedia(tabletQuery)
  media.addEventListener("change", onChange)
  return () => media.removeEventListener("change", onChange)
}

function tabletSnapshot() {
  return window.matchMedia(tabletQuery).matches
}

export function useTabletNotifications() {
  return useSyncExternalStore(subscribe, tabletSnapshot, () => false)
}

const panelFrame =
  "fixed right-4 z-40 overflow-y-auto rounded-[10px] border border-white/10 bg-black-800 p-3 shadow-xl"

type PanelProps = {
  open: boolean
  notifications: Notification[]
  onClose: () => void
  frameClassName: string
}

function Panel({ open, notifications, onClose, frameClassName }: PanelProps) {
  if (!open) return null

  const items = showNotificationMocks ? mockNotifications : notifications

  return (
    <>
      <button
        type="button"
        aria-label={screenTexts.close}
        className="fixed inset-0 z-40 cursor-default bg-transparent"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={screenTexts.filters.all}
        className={frameClassName}
        onKeyDown={(event) => {
          if (event.key === "Escape") onClose()
        }}
      >
        <NotificationsScreen notifications={items} />
      </div>
    </>
  )
}

type TabletPanelProps = {
  open: boolean
  notifications: Notification[]
  onClose: () => void
}

export function TabletPanel({
  open,
  notifications,
  onClose
}: TabletPanelProps) {
  return (
    <div className="hidden md:block xlg:hidden">
      <Panel
        open={open}
        notifications={notifications}
        onClose={onClose}
        frameClassName={`${panelFrame} scrollbar-hide top-[calc(4.5rem+16px)] h-fit max-h-[min(80%,calc(100%-4.5rem-16px-4.75rem))] ls:max-h-4/5 w-1/2`}
      />
    </div>
  )
}

export function LargePanel({ open, notifications, onClose }: TabletPanelProps) {
  const items = showNotificationMocks ? mockNotifications : notifications
  const empty = items.every((item) => item.deleted)
  const height = empty ? "h-fit" : "h-4/5"

  return (
    <Panel
      open={open}
      notifications={notifications}
      onClose={onClose}
      frameClassName={`${panelFrame} scrollbar-hide top-[calc(5rem+16px)] ${height} w-[402px]`}
    />
  )
}
